import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Mail, Plus, ExternalLink, Heart } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { parseCalendarDate } from '@/lib/parseCalendarDate';

interface Invitation {
  id: string;
  bride_name: string;
  groom_name: string;
  wedding_date: string;
  is_published: boolean;
  unique_slug: string;
  template_id: string;
}

interface InvitationsWidgetProps {
  invitations: Invitation[];
}

const InvitationsWidget = ({ invitations }: InvitationsWidgetProps) => {
  const latestInvitation = invitations[0];
  // TZ-estable: wedding_date es date-only guardado a medianoche UTC.
  const weddingDate = parseCalendarDate(latestInvitation?.wedding_date);

  return (
    <Card className="shadow-card border-border/50">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-blush/10 flex items-center justify-center">
              <Mail className="w-4 h-4 text-blush" />
            </div>
            Mis Invitaciones
          </CardTitle>
          <Button variant="ghost" size="sm" asChild className="text-primary">
            <Link to="/invitations/new">
              <Plus className="w-4 h-4 mr-1" />
              Nueva
            </Link>
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {!latestInvitation ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 rounded-full bg-muted/50 flex items-center justify-center mx-auto mb-4">
              <Heart className="w-8 h-8 text-muted-foreground/50" />
            </div>
            <p className="text-muted-foreground text-sm mb-4">
              Aún no has creado invitaciones
            </p>
            <Button asChild size="sm">
              <Link to="/invitations/new">
                Crear mi primera invitación
              </Link>
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="p-4 rounded-lg bg-gradient-to-br from-blush-light/30 to-sage-light/20 border border-primary/10">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h4 className="font-serif text-lg font-medium text-foreground">
                    {latestInvitation.bride_name} & {latestInvitation.groom_name}
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    {weddingDate && format(weddingDate, "d 'de' MMMM, yyyy", { locale: es })}
                  </p>
                </div>
                <Badge variant={latestInvitation.is_published ? "default" : "secondary"}>
                  {latestInvitation.is_published ? 'Publicada' : 'Borrador'}
                </Badge>
              </div>
              
              <div className="flex gap-2">
                <Button variant="outline" size="sm" asChild className="flex-1">
                  <Link to={`/invitations/${latestInvitation.id}/edit`}>
                    Editar
                  </Link>
                </Button>
                {latestInvitation.is_published && (
                  <Button variant="outline" size="sm" asChild>
                    <Link to={`/i/${latestInvitation.unique_slug}`} target="_blank">
                      <ExternalLink className="w-4 h-4" />
                    </Link>
                  </Button>
                )}
              </div>
            </div>

            {invitations.length > 1 && (
              <Button variant="link" className="w-full text-primary text-sm" asChild>
                <Link to="/dashboard/invitations">
                  Ver todas ({invitations.length})
                </Link>
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default InvitationsWidget;
